import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import logo from '../logo.png';
import CreatedBy from '../components/CreatedBy';

function formatBytes(bytes) {
  if (!bytes) return '—';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ja-JP', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function DownloadPage() {
  const { fileId } = useParams();
  const [fileInfo, setFileInfo] = useState(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`/api/files/info.php?fileId=${fileId}`)
      .then((res) => setFileInfo(res.data))
      .catch((err) => {
        if (err.response?.status === 410) setError('このファイルは有効期限が切れています');
        else if (err.response?.status === 404) setError('ファイルが見つかりません');
        else setError(err.response?.data?.error || 'ファイル情報の取得に失敗しました');
      });
  }, [fileId]);

  const handleDownload = async () => {
    setDownloading(true);
    setError('');
    try {
      const verifyRes = await axios.post(`/api/files/verify.php?fileId=${fileId}`, {
        password: fileInfo?.hasPassword ? password : undefined,
      });
      const { token } = verifyRes.data;
      const a = document.createElement('a');
      a.href = `/api/files/download.php?fileId=${fileId}&token=${token}`;
      a.download = fileInfo?.originalName || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setDone(true);
    } catch (err) {
      if (err.response?.status === 401) setError('パスワードが違います');
      else setError(err.response?.data?.error || 'ダウンロードに失敗しました');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ background: '#F5F2EC' }}>
      <CreatedBy />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <a href="/" className="inline-block" style={{ cursor: 'pointer' }}>
            <img src={logo} alt="and to" style={{ width: '120px', display: 'block' }} />
            <p className="text-xs uppercase text-center" style={{ color: '#6E6A62', width: '120px', marginTop: '8px', letterSpacing: '0.2em', whiteSpace: 'nowrap' }}>
              FILE TRANSFER
            </p>
          </a>
        </div>

        <div
          className="rounded-2xl p-8"
          style={{
            background: '#FDFAF5',
            border: '1px solid #E2DDD4',
            boxShadow: '0 4px 24px rgba(45,42,36,0.07)',
          }}
        >
          {error && !fileInfo ? (
            <div className="text-center py-4">
              <p className="text-2xl mb-3">—</p>
              <p className="text-sm font-medium" style={{ color: '#2D2A24' }}>{error}</p>
            </div>
          ) : !fileInfo ? (
            <div className="py-8">
              <style>{`@keyframes slide { 0%{left:-40%} 100%{left:110%} }`}</style>
              <div style={{ position: 'relative', width: '100%', height: '1px', background: '#E2DDD4', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', height: '1px', width: '40%', background: '#2D2A24', animation: 'slide 1.6s ease-in-out infinite' }} />
              </div>
            </div>
          ) : done ? (
            <div className="text-center">
              <div className="flex items-center justify-center w-14 h-14 rounded-full mx-auto mb-4"
                style={{ background: '#E8F0E9', border: '1.5px solid #6B8F71' }}>
                <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: '#6B8F71' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-base font-bold mb-1" style={{ color: '#2D2A24' }}>ダウンロードを開始します</p>
              <p className="text-xs" style={{ color: '#6E6A62' }}>ブラウザのダウンロードをご確認ください</p>
              <button
                onClick={() => { setDone(false); setPassword(''); }}
                className="mt-5 text-xs hover:underline"
                style={{ color: '#C8694A' }}
              >
                もう一度ダウンロード
              </button>
            </div>
          ) : (
            <>
              {/* File info */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: '#F5F2EC', border: '1px solid #E2DDD4' }}>
                  <span className="text-xl">📄</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm break-all leading-tight" style={{ color: '#2D2A24' }}>
                    {fileInfo.originalName}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: '#6E6A62' }}>{formatBytes(fileInfo.size)}</p>
                </div>
              </div>

              {/* Metadata */}
              <div className="rounded-xl p-4 mb-6 space-y-2"
                style={{ background: '#F5F2EC', border: '1px solid #E2DDD4' }}>
                <div className="flex justify-between text-xs">
                  <span style={{ color: '#6E6A62' }}>有効期限</span>
                  <span className="font-medium" style={{ color: '#2D2A24' }}>{formatDate(fileInfo.expiresAt)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span style={{ color: '#6E6A62' }}>ダウンロード数</span>
                  <span className="font-medium" style={{ color: '#2D2A24' }}>{fileInfo.downloadCount} 回</span>
                </div>
                {fileInfo.hasPassword && (
                  <div className="flex justify-between text-xs">
                    <span style={{ color: '#6E6A62' }}>パスワード</span>
                    <span className="font-medium" style={{ color: '#C8694A' }}>保護あり</span>
                  </div>
                )}
              </div>

              {/* Password input */}
              {fileInfo.hasPassword && (
                <div className="mb-5">
                  <label className="text-xs font-semibold uppercase tracking-widest block mb-2" style={{ color: '#6E6A62' }}>
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleDownload()}
                    placeholder="パスワードを入力"
                    className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none"
                    style={{ background: '#F5F2EC', border: '1px solid #E2DDD4', color: '#2D2A24' }}
                  />
                </div>
              )}

              {error && (
                <p className="text-xs mb-4 text-center" style={{ color: '#C8694A' }}>{error}</p>
              )}

              {downloading && (
                <div className="mb-4">
                  <style>{`@keyframes slide { 0%{left:-40%} 100%{left:110%} }`}</style>
                  <p className="text-xs mb-2" style={{ color: '#6E6A62' }}>準備中...</p>
                  <div style={{ position: 'relative', width: '100%', height: '1px', background: '#E2DDD4', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', height: '1px', width: '40%', background: '#2D2A24', animation: 'slide 1.6s ease-in-out infinite' }} />
                  </div>
                </div>
              )}

              <button
                onClick={handleDownload}
                disabled={downloading || (fileInfo.hasPassword && !password)}
                className="w-full py-3 rounded-xl font-semibold text-sm tracking-wide transition-all duration-200 active:scale-[0.98]"
                style={
                  !downloading && (!fileInfo.hasPassword || password)
                    ? { background: '#2D2A24', color: '#F5F2EC' }
                    : { background: '#E2DDD4', color: '#6E6A62', cursor: 'not-allowed' }
                }
              >
                {downloading ? '準備中...' : 'ダウンロード'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
